import { VpQuestionIcon } from '@vtmn-play/icons/react/index'
import { VpActionChip, VpAsset, VpIcon, VpIconButton, VpNavigationHeader, VpNavigationHeaderRow } from '@vtmn-play/react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Link, useMatch, useNavigate } from 'react-router'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { useScrollContext } from '@/contexts/scroll/useScrollContext'
import { environment } from '@/environments'
import { useFavoriteAccess } from '@/hooks/favorites/useFavoriteAccess'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import { NavigationHeaderSearch } from './NavigationHeaderSearch'
import './navigation-header.scss'

export function NavigationHeader() {
  const navigate = useNavigate()
  const { hideHeader } = useScrollContext()
  const { t } = useTranslation()
  const hasFavoriteAccess = useFavoriteAccess()

  const exactSeasonMatch = useMatch('/:season')
  const deepSeasonMatch = useMatch('/:season/*')
  const routeSeason = (deepSeasonMatch ?? exactSeasonMatch)?.params?.season ?? null
  const currentSeason = routeSeason && /^(?:SS|AW)\d{2}$/.test(routeSeason) ? routeSeason : null

  return (
    <div className={clsx('navigation-header', { 'navigation-header--hidden': hideHeader })}>
      <VpNavigationHeader>
        <VpNavigationHeaderRow>
          <Link className="vp-navigation-header__logo" to="/">
            <VpAsset name="logo-decathlon-lockup-primary-brand" />
            <span aria-hidden="true">|</span>
            <span>Offer Catalog</span>
          </Link>
          <div className="vp-navigation-header__search">
            <NavigationHeaderSearch currentSeason={currentSeason} />
          </div>
          <VpActionChip
            data-testid="season-tag"
            role="link"
            onClick={async () => {
              trackAnalyticsEvent(ANALYTICS_EVENTS.SEASON_TAG_CLICKED, { current_season: currentSeason })
              await navigate('/')
            }}
            disabled={!currentSeason}
            aria-label={currentSeason
              ? t('aria-label.selected-season-link', { season: currentSeason })
              : undefined}
          >
            {currentSeason
              ? (
                  <span>
                    {t(`season-selection.${currentSeason.startsWith('SS') ? 'ss' : 'aw'}`)}
                    {currentSeason.slice(2)}
                  </span>
                )
              : t('season-selection.no-season')}
          </VpActionChip>
          <div className="vp-navigation-header__actions">
            {environment.env && environment.env !== 'production' && (
              <div
                data-testid="env-banner"
                className={clsx('catalog-env-banner', environment.env)}
              >
                {environment.env}
              </div>
            )}
            {hasFavoriteAccess && (
              <VpIconButton
                onClick={() => navigate('/favorites')}
                aria-label={t('favorite-views.open')}
                name="favorite-views"
                variant="tertiary"
                data-testid="navigation-favorites-icon"
                size="medium"
              >
                <VpIcon name="heart" />
              </VpIconButton>
            )}
            {environment.beamerId && (
              <VpIconButton
                className="beamerTrigger"
                aria-label={t('aria-label.open-product-updates')}
                data-testid="navigation-beamer-icon"
                size="medium"
                variant="tertiary"
              >
                <VpIcon name="notification" />
              </VpIconButton>
            )}
            <LanguageSwitcher />
            <VpIconButton
              onClick={() => window.open(environment.helpUrl, '_blank', 'noopener,noreferrer')}
              aria-label={t('aria-label.open-help-page')}
              name="help"
              variant="tertiary"
              data-testid="navigation-help-icon"
              size="small"
            >
              <VpQuestionIcon />
            </VpIconButton>
          </div>
        </VpNavigationHeaderRow>
      </VpNavigationHeader>
    </div>
  )
}
